import axios from 'axios';
import { auth } from './firebase/firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://us-central1-sail-dev-62f69.cloudfunctions.net/app';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth headers
api.interceptors.request.use((config) => {
    // Add any default headers here if needed
    config.headers.key = 'wizard-key';
    config.headers.appkey = process.env.REACT_APP_CLIENT_KEY || 'wizard-key';
    
    return config;
});

/**
 * Get user token for Firebase authentication
 * @param {Object} data - Data object containing uid
 * @returns {Promise} API response
 */
export const getUserToken = (data) => {
    // Try with the same headers as firstAgent API
    return axios.post(`${API_BASE_URL}/login`, data, {
        headers: {
            'Content-Type': 'application/json',
            'key': 'wizard-key',
            'appkey': 'wizard-key', // Try using wizard-key as appkey too
            // Add the same permissions as firstAgent
            'userpermissions': JSON.stringify([
                "sail_reset-provider","sail_new_email","sail_new_conversation","sail_live_chat","sail_consumption_data","sail_subscription_data","sail_list_user-live-agent","sail_list_documents","sail_list_chatbot_skills","sail_list_intent_data","sail_list_intents","sail_list_skills","sail_list_chatbots","sail_access_admin","sail_access","access_sail_v2","sail_named_user","sail_admin_user","sail_admin_user","sail_named_user","sail_toggle_document","sail_update_client_everywhere","sail_clear_unresolved_alerts","sail_qa_user","sail_edit_document","sail_delete_document","sail_upload_url","sail_upload_document","sail_sync_intents","sail_move_intent","sail_update_intent_response","sail_update_intent_option","sail_update_intent_phrase","sail_delete_intent_response","sail_create_intent_option","sail_create_intent_response","sail_delete_intent_option","sail_delete_intent_phrase","sail_create_intent_phrase","sail_delete_intent","sail_update_intent","sail_create_intent","sail_edit_skill","sail_update_skill","sail_add_skill","sail_create_skill","sail_remove_skill","sail_delete_skill","sail_update_translation_chat","sail_update_mangage_chat","sail_add_claim","sail_delete_contact","sail_update_contact","sail_reset-provider","sail_new_email","sail_new_conversation","sail_update_urls","sail_create_chatbot","sail_delete_chatbot","sail_update_chatbot_escalation","sail_update_chatbot_colour","sail_delete_chatbot_apology","sail_add_chatbot_apology","sail_update_chatbot_apology","sail_delete_chatbot_options","sail_add_chatbot_option","sail_update_chatbot_option","sail_delete_chatbot_greeting","sail_add_chatbot_greeting","sail_update_chatbot_greeting","sail_update_chatbot_status","sail_consumption_data","sail_subscription_data","sail_list_access_skill","sail_list_emails","sail_get_contact_message","sail_get_contact","sail_list_contacts","sail_list_user-live-agent","sail_analytics_chatbot_escalation","sail_analytics_chatbot_popularity","sail_analytics_chat_duration","sail_list_chatbot_escalations","sail_list_chatbot_colours","sail_list_chatbot_apologies","sail_list_chatbot_options","sail_list_chatbot_greetings","sail_list_documents","sail_list_chatbot_skills","sail_list_intent_data","sail_list_intents","sail_list_skills","sail_list_chatbots","sail_access_help","sail_access_subscription","sail_access_contact_list","sail_access_analytics","sail_access_admin","sail_access","access_sail_v2","sail_edit_intent","sail_update_chatbot","Access_Form_Test","Access_AI_Credentials_638735698361449640","Access_AI_Tools_638735698088074485","Access_AI_Agentflows_638735697809044142","Access_AI_Chatflows_638735695110424298","Access_AI_Document_Stores_638760612207367954","Access_AI_API_Keys_638761542246653132","Access_Workflow_V3_Environment_Variables_638715012302554161","Access_Workflow_V3_Registry_638715011960548684","Access_Workflow_V3_Instances_638715010498527937","Access_Workflow_V3_Definitions_638715009941391465","Access_Forms","Access Create Page","Access Page Details","Access Structure Card Page","Access Pages Card Page","Access Create Page End User Page","Access Edit Page End User","Access Create App Page","Access App Details Page","access apps and pages application","Access_End_User_Preview_Page_638392662695163639","Access_Theme_For_Apps_And_Pages","Access_Version_List","Access_Breadcrumb_Permission_638529960111065394","Access_Document_Management_v2","Access_Operational_Stack_Application_638478046246443733","Access_Ingestion_Pipelines_638478060357819301","Access_Extraction_Pipelines_638478067788608984","Access_Ingestion_Data_Source_Upload_Excel_Progress_638508173846858250","Access_Ingestion_Data_Source_Upload_Excel_638508172520696252","Access_Ingestion_Data_Mapping_638508170208028665","Access_Ingestion_Data_Source_Update_638508169545117506","Access_Ingestion_Data_Source_Create_638508168043090488","Access_Ingestion_Data_Source_638508166973717273","Access_Ingestion_Data_Source_View_638508202478553270","Access_Process_Log_638560093844933165","Access_Activity_Log_638560093124475327","Access_Extraction_Progress_638552305767284338","Access_Extraction_Data_638552302893327726","Access_Extraction_Settings_638552297088554350","Access_Extraction_Data_Mapping_Update_638552294842017023","Access_Extraction_Data_Mapping_Create_638552289910277737","Access_Extraction_Data_Extractions_638552279229246004","access delete checkbox"
            ])
        }
    });
};

/**
 * Update chatbot (e.g., set status online)
 * @param {string} id - Chatbot ID
 * @param {Object} data - Partial chatbot data to update, e.g., { status: 'online' }
 * @returns {Promise} API response
 */
export const updateChatbot = (id, data) => {
    const userPermissions = [
        "sail_update_chatbot_status",
        "sail_update_chatbot",
        // Keep broader set to match other calls using wizard
        "sail_update_chatbot_greeting","sail_add_chatbot_greeting","sail_delete_chatbot_greeting",
        "sail_update_chatbot_option","sail_add_chatbot_option","sail_delete_chatbot_options",
        "sail_update_chatbot_colour","sail_update_chatbot_escalation",
    ];

    return axios.patch(`${API_BASE_URL}/extChatbots/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            'key': 'wizard-key',
            'appkey': process.env.REACT_APP_CLIENT_KEY || 'wizard-key',
            'userpermissions': JSON.stringify(userPermissions),
        },
    });
};

/**
 * Create first agent
 * @param {FormData} formData - Form data containing agent details and files
 * @param {string} progressId - Optional progress ID for real-time tracking
 * @param {string} clientId - Optional client ID from JWT token for admin panel integration
 * @returns {Promise} API response
 */
export const createFirstAgent = async (formData, progressId = null, clientId = null) => {
    try {
        // Get Firebase ID token for authentication
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated. Please sign in first.');
        }

        const idToken = await currentUser.getIdToken();
        
        // Legacy headers for backward compatibility (optional now)
        const generateAppKey = () => {
            return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
        };

        // Use clientId from JWT token when available, otherwise generate random appkey
        const appkey = clientId || generateAppKey();
        
        if (clientId) {
            console.log('[JWT_INTEGRATION] Using clientId from JWT token as appkey:', clientId);
        } else {
            console.log('[LEGACY_MODE] Generated random appkey for non-JWT user');
        }

        const headers = {
            'Authorization': `Bearer ${idToken}`, // Firebase authentication
            'key': 'wizard-key', // Legacy support
            'appkey': appkey, // Legacy support
            // Don't set Content-Type - let axios handle it for FormData
        };

        // Add progress ID for real-time tracking
        if (progressId) {
            headers['x-progress-id'] = progressId;
            console.log('[PROGRESS] Adding progress ID to request:', progressId);
        }

        const response = await axios.post(`${API_BASE_URL}/firstAgentExt`, formData, {
            headers,
        });

        return response;
    } catch (error) {
        console.error('Error creating first agent:', error);
        
        // Provide more specific error messages
        if (error.message.includes('not authenticated')) {
            throw new Error('Authentication required. Please refresh the page and try again.');
        } else if (error.response?.status === 401) {
            throw new Error('Authentication failed. Please refresh the page and try again.');
        } else if (error.response?.status === 429) {
            // Handle rate limiting with specific retry time
            const retryAfter = error.response?.data?.retryAfter || 3600; // Default 1 hour
            const retryMinutes = Math.ceil(retryAfter / 60);
            throw new Error(`RATE_LIMITED:${retryMinutes}:Too many requests from your IP. Please try again in ${retryMinutes} minutes.`);
        }
        
        throw error;
    }
};

/**
 * Send help email for White Glove Service
 * @param {Object} data - Email data containing user email and message
 * @returns {Promise} API response
 */
export const helpEmail = async (data) => {
    try {
        // Note: Help email endpoint uses rate limiting instead of Firebase auth
        // since it's designed to be accessible without authentication
        const generateAppKey = () => {
            return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
        };

        const appkey = generateAppKey();

        const response = await axios.post(`${API_BASE_URL}/firstAgent/helpEmailExt`, data, {
            headers: {
                'Content-Type': 'application/json',
                'key': 'wizard-key',
                'appkey': appkey,
            },
        });

        return response;
    } catch (error) {
        console.error('Error sending help email:', error);
        
        // Provide more specific error messages
        if (error.response?.status === 429) {
            const retryAfter = error.response?.data?.retryAfter || 3600; // Default 1 hour
            const retryMinutes = Math.ceil(retryAfter / 60);
            throw new Error(`Too many help requests from your IP. Please try again in ${retryMinutes} minutes.`);
        }
        
        throw error;
    }
};

/**
 * Publish agent using the new publishAgentExt API
 * @param {Object} publishData - Comprehensive publish data from the wizard
 * @returns {Promise} API response
 */
export const publishAgent = async (publishData) => {
    try {
        console.log('Publishing agent with data:', publishData);
        
        // Get Firebase ID token for authentication
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated. Please sign in first.');
        }

        const idToken = await currentUser.getIdToken();
        
        // Detect user's timezone
        const getUserTimezone = () => {
            try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch (error) {
                console.warn('Failed to detect timezone, using default:', error);
                return 'America/Toronto'; // Fallback timezone
            }
        };

        // Prepare data for the new publish API endpoint
        const publishPayload = {
            email: publishData.userDetails.email,
            firstName: publishData.userDetails.firstName || 'User', // Default if not provided
            lastName: publishData.userDetails.lastName || 'Agent', // Default if not provided  
            company: publishData.userDetails.company || publishData.userDetails.email.split('@')[1] || 'Unknown Company', // Extract from email domain if not provided
            chatbotId: publishData.chatbotId,
            skillId: publishData.skillId || publishData.chatbotId, // Use chatbotId as fallback for skillId
            chatbotName: publishData.chatbotName,
            timezone: getUserTimezone(), // Auto-detect user's timezone
            
            // Admin panel integration fields
            clientId: publishData.clientId || null,
            prefilledFromAdmin: publishData.prefilledFromAdmin || false
        };

        console.log('Calling publishAgentExt API with payload:', publishPayload);

        // Prepare headers
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'X-Progress-ID': publishData.chatbotId // For progress tracking if needed
        };

        // Add wizard token if available (for admin panel integration)
        const wizardToken = sessionStorage.getItem('wizardToken');
        if (wizardToken) {
            headers['X-Wizard-Token'] = wizardToken;
            console.log('🔐 Adding wizard token to publish request for admin panel integration');
        }

        const response = await axios.post(`${API_BASE_URL}/publishAgentExt/publish`, publishPayload, {
            headers,
        });

        console.log('Publish API response:', response.data);
        return response;
        
    } catch (error) {
        console.error('Error publishing agent:', error);
        
        // Provide more specific error messages
        if (error.message.includes('not authenticated')) {
            throw new Error('Authentication required. Please refresh the page and try again.');
        } else if (error.response?.status === 401) {
            throw new Error('Authentication failed. Please refresh the page and try again.');
        } else if (error.response?.status === 429) {
            // Handle rate limiting with specific retry time
            const retryAfter = error.response?.data?.retryAfter || 3600; // Default 1 hour
            const retryMinutes = Math.ceil(retryAfter / 60);
            throw new Error(`Too many requests from your IP. Please try again in ${retryMinutes} minutes.`);
        } else if (error.response?.status === 400) {
            // Handle validation or subscription limit errors
            const errorMessage = error.response?.data?.message || 'Invalid data provided. Please check your information.';
            throw new Error(errorMessage);
        } else if (error.response?.data?.userFriendly) {
            // Handle user-friendly error messages from the API
            throw new Error(error.response.data.message);
        }
        
        throw error;
    }
};

/**
 * Send email verification code
 * @param {Object} data - Verification data containing email and optional clientId
 * @returns {Promise} API response
 */
export const sendVerificationCode = async (data) => {
    try {
        const { email, clientId } = data;
        
        console.log('📧 Sending verification code to:', email);
        
        // Get current user and token (following existing pattern)
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated. Please sign in first.');
        }
        
        const idToken = await currentUser.getIdToken();
        
        const response = await axios.post(`${API_BASE_URL}/emailVerification/sendCode`, {
            email,
            clientId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            timeout: 30000 // 30 second timeout
        });

        console.log('✅ Verification code sent successfully');
        return response.data;
        
    } catch (error) {
        console.error('❌ Send verification code failed:', error);
        
        if (error.response?.status === 429) {
            const retryAfter = error.response?.data?.retryAfter || 3600;
            const retryMinutes = Math.ceil(retryAfter / 60);
            throw new Error(`RATE_LIMITED:${retryMinutes}:${error.response.data.message}`);
        }
        
        if (error.response?.status === 400) {
            throw new Error(`VALIDATION_ERROR:${error.response.data.message}`);
        }
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error('TIMEOUT:Request timed out. Please try again.');
        }
        
        throw new Error(`SEND_CODE_ERROR:${error.response?.data?.message || 'Failed to send verification code'}`);
    }
};

/**
 * Verify email code
 * @param {Object} data - Verification data containing email, code, and optional sessionId
 * @returns {Promise} API response
 */
export const verifyEmailCode = async (data) => {
    try {
        const { email, code, sessionId } = data;
        
        console.log('🔐 Verifying email code for:', email);
        
        // Get current user and token (following existing pattern)
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated. Please sign in first.');
        }
        
        const idToken = await currentUser.getIdToken();
        
        const response = await axios.post(`${API_BASE_URL}/emailVerification/verifyCode`, {
            email,
            code,
            sessionId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            timeout: 15000 // 15 second timeout
        });

        console.log('✅ Email verification successful');
        return response.data;
        
    } catch (error) {
        console.error('❌ Email verification failed:', error);
        
        if (error.response?.status === 429) {
            const retryAfter = error.response?.data?.retryAfter || 3600;
            const retryMinutes = Math.ceil(retryAfter / 60);
            throw new Error(`RATE_LIMITED:${retryMinutes}:${error.response.data.message}`);
        }
        
        if (error.response?.status === 400) {
            const errorData = error.response.data;
            if (errorData.expired || errorData.needsNewCode) {
                throw new Error(`CODE_EXPIRED:${errorData.message}`);
            }
            if (errorData.attemptsRemaining !== undefined) {
                throw new Error(`INVALID_CODE:${errorData.message}:${errorData.attemptsRemaining}`);
            }
            throw new Error(`VALIDATION_ERROR:${errorData.message}`);
        }
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error('TIMEOUT:Request timed out. Please try again.');
        }
        
        throw new Error(`VERIFY_CODE_ERROR:${error.response?.data?.message || 'Failed to verify code'}`);
    }
};

/**
 * Check email verification status
 * @param {Object} data - Data containing email and optional sessionId
 * @returns {Promise} API response
 */
export const checkVerificationStatus = async (data) => {
    try {
        const { email, sessionId } = data;
        
        // Get current user and token (following existing pattern)
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('User not authenticated. Please sign in first.');
        }
        
        const idToken = await currentUser.getIdToken();
        
        const response = await axios.post(`${API_BASE_URL}/emailVerification/checkStatus`, {
            email,
            sessionId
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            timeout: 10000 // 10 second timeout
        });

        return response.data;
        
    } catch (error) {
        console.error('❌ Check verification status failed:', error);
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error('TIMEOUT:Request timed out. Please try again.');
        }
        
        throw new Error(`STATUS_CHECK_ERROR:${error.response?.data?.message || 'Failed to check verification status'}`);
    }
};

export default api;
